'use client'
import { useRef, useState } from 'react'
import * as yup from 'yup'
import { useFormik } from 'formik';
import { env } from '@/env.mjs'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-react'
import { TextInput } from '@tremor/react'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import YupPassword from 'yup-password'
import { createClient } from '@/utils/supabase/client';
import PasswordConditionsHelper from '@/components/auth/PasswordConditionsHelper';
import { memo } from "react";
import Cookies from 'js-cookie';

async function checkEmailUnique(email) {
  const response = await fetch(`/api/signup/validate/user?email=${email}`);
  const data = await response.json();
  return !data.exists;
}

async function checkEinUnique(ein) {
  const response = await fetch(`/api/signup/validate/business?ein=${ein}`);
  const data = await response.json();
  return !data.exists;
}


// extend yup with password validation
YupPassword(yup)
const BusinessSignUpForm = () => {
  const supabase = createClient()

  const [showConditions, setShowConditions] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSigningUp, setIsSigningUp] = useState(false)
  const { toast } = useToast()
  const einPattern = /^\d{2}-\d{7}$/;

  const referralCode = Cookies.get('referral_code');

  const formik = useFormik({
    initialValues: {
      companyName: '',
      firstName: '',
      lastName: '',
      ein: '',
      email: '',
      password: '',
      submit: null,
    },
    validationSchema: yup.object({
      companyName: yup.string().required('Company name is required'),
      firstName: yup.string().required('First name is required'),
      lastName: yup.string().required('Last name is required'),
      email: yup.string()
        .required('Email is required')
        .email('Invalid email')
        .test('checkEmailUnique', 'Email already exists', async (value) => {
          if (value) {
            return await checkEmailUnique(value);
          }
          return true;
        }),
      password: yup.string().password().required().label('Password'),
      ein: yup.string()
        .required('EIN is required')
        .test('isValidEIN', 'Invalid EIN format', value => einPattern.test(value))
        .test('checkEinUnique', 'EIN already exists', async (value) => {
          if (value) {
            return await checkEinUnique(value);
          }
          return true;
        }),
    }),
    onSubmit: async (values, helpers) => {
      setIsSigningUp(true)
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          emailRedirectTo: `${env.NEXT_PUBLIC_APP_URL}`,
          data: {
            companyName: values.companyName,
            first_name: values.firstName,
            last_name: values.lastName,
            ein: values.ein,
            referral_code: referralCode || null
          }
        }
      })
      if (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: `Failed to sign up: ${error.message}`,
        })
        setIsSigningUp(false)

      } else {
        toast({
          title: 'Success',
          description: 'Check your email to confirm your account',
        })
        setIsSubmitted(true)
      }
    },
  });


  return (
    <div className="relative">
      <div
        className={`absolute top-0 w-full delay-300 duration-500 ${isSubmitted ? 'opacity-100' : 'opacity-0'
          }`}
      >
        <Alert className="w-full" title="Check your email to confirm">
          <AlertTitle>  Check your email to confirm</AlertTitle>
          <AlertDescription>
            {`You've successfully signed up. Please check your email to confirm your account before
            signing in to the Relistex`}
          </AlertDescription>
        </Alert>
      </div>
      <form noValidate onSubmit={formik.handleSubmit} className={`w-full overflow-y-hidden py-1 transition-all duration-500 ${isSubmitted ? 'pointer-events-none max-h-[100px] opacity-0' : 'max-h-[1000px] opacity-100'
        }`}>
        <div className="flex flex-col gap-4">
          <div className="grid gap-2">
            {/* Add company name */}
            {/* add first name last name */}

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="firstName">First Name</Label>
                <TextInput
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder=''
                  errorMessage={formik.touched.firstName && formik.errors.firstName || ''}
                  error={Boolean(formik.touched.firstName && formik.errors.firstName)}
                  disabled={isSigningUp}
                  defaultValue={formik.values.firstName}
                  onValueChange={(value) => {
                    formik.setFieldValue('firstName', value)
                  }}
                  autoComplete="firstName"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="lastName">Last Name</Label>
                <TextInput
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder=''
                  errorMessage={formik.touched.lastName && formik.errors.lastName || ''}
                  error={Boolean(formik.touched.lastName && formik.errors.lastName)}
                  disabled={isSigningUp}
                  defaultValue={formik.values.lastName}
                  onValueChange={(value) => {
                    formik.setFieldValue('lastName', value)
                  }}
                  autoComplete="lastName"
                />
              </div>
            </div>


          </div>
          <div
            className="grid gap-2"
          >
            <div className="flex space-x-1">
              <Label htmlFor="companyName">Company Name</Label>
            </div>
            <TextInput
              id="companyName"
              name="companyName"
              type="text"
              errorMessage={formik.touched.companyName && formik.errors.companyName || ''}
              error={Boolean(formik.touched.companyName && formik.errors.companyName)}
              placeholder=""
              disabled={isSigningUp}
              defaultValue={formik.values.companyName}
              onValueChange={(value) => {
                formik.setFieldValue('companyName', value)
              }}
              autoComplete="companyName"
            />
          </div>
          {/* add EIN number */}
          <div className="grid gap-2">
            <div className="flex space-x-1">
              <Label htmlFor="ein">EIN</Label>
            </div>
            <TextInput
              id="ein"
              name="ein"
              type="text"
              errorMessage={formik.touched.ein && formik.errors.ein || ''}
              error={Boolean(formik.touched.ein && formik.errors.ein)}
              placeholder=""
              disabled={isSigningUp}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              defaultValue={formik.values.ein}
              value={formik.values.ein}
              autoComplete="ein"
            />

            <div className="grid gap-2">
              <div className="flex space-x-1">
                <Label htmlFor="subject">Work Email</Label>
              </div>
              <TextInput
                id="email"
                name="email"
                type="email"
                errorMessage={formik.touched.email && formik.errors.email || ''}
                error={Boolean(formik.touched.email && formik.errors.email)}
                placeholder="you@example.com"
                disabled={isSigningUp}
                onChange={formik.handleChange}
                defaultValue={formik.values.email}
                value={formik.values.email}
                onBlur={formik.handleBlur}
                autoComplete="email"
              />
            </div>

            <div className="grid gap-2">
              <div className="flex space-x-1">
                <Label htmlFor="subject">Password</Label>
              </div>

              <TextInput
                id="password"
                name="password"
                type={'password'}
                placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
                disabled={isSigningUp}
                errorMessage={formik.touched.password && formik.errors.password || ''}
                error={Boolean(formik.touched.password && formik.errors.password)}
                defaultValue={formik.values.password}
                onValueChange={(value) => {
                  formik.setFieldValue('password', value)
                }}
                autoComplete="new-password"
                onFocus={() => setShowConditions(true)}
              />
            </div>

            <div
              className={`${showConditions ? 'max-h-[500px]' : 'max-h-[0px]'
                } duration-400 overflow-y-hidden transition-all`}
            >
              <PasswordConditionsHelper password={formik.values.password} />
            </div>

            <Button
              type='submit'
              size="sm"
              disabled={formik.values.password.length === 0 || isSigningUp}
            >
              {isSigningUp && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}  Sign Up
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default memo(BusinessSignUpForm)
